import { Button, Checkbox, Input, message, Modal, Popconfirm, Select, Space, Table, Tabs, Typography } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fArrayNumberToLocalString, getIdFromObject } from 'utils';
import { createCategory, createEstSalesVersion, deleteCategory, fetchAllCategories, fetchAllEstSalesVersions, updateEstSalesVersion } from 'services';
import { Helmet } from 'react-helmet';
import { marketplaces } from 'helpers';

const { TabPane } = Tabs;

interface DataType {
  _id: string;
  category: string;
  rangeRank: string;
  a: string;
  b: number;
  c: number;
}

const newVersionState: any = {
  name: null,
  isCurrent: true,
};

const defaultCategory = {
  version: '',
  marketplace: '',
  category: '',
  rankRange: [],
  a: [],
  b: 0,
  c: 0,
  draftParams: {},
  currentVersion: false,
}

const EstimateSales = () => {
  let { location, version } = useParams();

  const [dataTable, setDataTable] = useState<any>();
  const [activedTab, setActivedTab] = useState<string>('');
  const [categoryName, setCategoryName] = useState("");
  const [openModalCreateNewVersion, setOpenModalCreateNewVersion] = useState(false);
  const [openModalChangeVersionName, setOpenModalChangeVersionName] = useState(false);
  const [newVersion, setNewVersion] = useState(newVersionState);
  const [versions, setVersions] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<string>(version || '');

  let navigate = useNavigate();

  useEffect(() => {
    if (location) {
      setActivedTab(location)
    }
  }, [location])

  useEffect(() => {
    setSelectedVersion(version || '')
  }, [version])

  useEffect(() => {
    if (activedTab) loadVersions(activedTab, selectedVersion)
  }, [activedTab, selectedVersion])

  const columns: ColumnsType<DataType> = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: text => <Link to={`../${activedTab}/${selectedVersion}/${text}`} target="_blank" rel="noopener noreferrer">{text}</Link>,
    },
    {
      title: 'Range rank',
      dataIndex: 'rangeRank',
      key: 'rangeRank',
      render: value => fArrayNumberToLocalString(value).join(' | ')
    },
    {
      title: 'a',
      dataIndex: 'a',
      key: 'a',
      render: value => fArrayNumberToLocalString(value).join(' | ')
    },
    {
      title: 'b',
      dataIndex: 'b',
      key: 'b',
    },
    {
      title: 'c',
      dataIndex: 'c',
      key: 'c',
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary"><Link to={`../${activedTab}/${selectedVersion}/${record.category}`} target="_blank" rel="noopener noreferrer">Edit</Link></Button>
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this category?"
            onConfirm={() => handleOnDeleteCategory(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" icon={<DeleteFilled />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const loadListOfCategories = (versionEstmateSale: string = currentVersion) => {
    setLoadingTable(true)
    return fetchAllCategories({ version: versionEstmateSale, marketplace: activedTab })
      .then(({ data }: any) => {
        const categories = data.categories.map((c: any, index: number) => {
          return ({
            _id: typeof c._id === 'object' ? getIdFromObject(c) : c._id,
            category: c.category,
            rangeRank: c.rankRange,
            a: c.a,
            b: c.b,
            c: c.c,
          })
        })
        setDataTable(categories);
      })
      .catch(() => message.error('Can not load the list of categories'))
      .finally(() => setLoadingTable(false));
    }

  const handleOnChangeTab = (tabKey: string) => {
    navigate(`../${tabKey}`);
  };

  const handleOnInputCategory = (event: any) => setCategoryName(event.target.value);
  
  const handleOnAddCategory = () => {
    if (categoryName.length > 0) {
      setLoadingTable(true);
      const params = {
        ...defaultCategory,
        version: selectedVersion,
        marketplace: activedTab,
        category: categoryName,
        currentVersion: currentVersion === selectedVersion
      }
      return createCategory(params)
        .then(() => {
          message.success('Created category succesfully');
          navigate(`../${activedTab}/${selectedVersion}/${categoryName}`)
        })
        .catch(() => message.error('Failed create category'))
        .finally(() => setLoadingTable(false))
    }
  }

  const handleOnDeleteCategory = (categoryId: string) => {
    setLoadingTable(true)
    return deleteCategory(categoryId)
    .then(() => {
        message.success('Deleted category succesfully')
        loadListOfCategories(version)
      })
      .catch(() => message.error('Failed delete category'));
  };

  const loadVersions = (marketplace: string, versionSelect?: string) => {
    setLoading(true)
    return fetchAllEstSalesVersions(marketplace)
      .then(({ data }: any) => {

        let tempSelectedVersion: any = versionSelect;
        const hasCurrentVersion = data.versions.filter((vm: any) => vm.isCurrentVersion);

        const versions = data.versions.map((item: any, index: number) => {
          if (!tempSelectedVersion) {
            tempSelectedVersion = hasCurrentVersion ? hasCurrentVersion[0].version : data.versions[0].version;
            setCurrentVersion(tempSelectedVersion);
          }

          return ({
            key: `version-${marketplace}-${index}`,
            label: item.isCurrentVersion ? `${item.version} (current)` : item.version,
            value: item.version,
            isCurrent: item.isCurrentVersion,
          });
        })
        loadListOfCategories(tempSelectedVersion);
        setVersions(() => {
          setSelectedVersion(tempSelectedVersion);
          return versions;
        });
      })
      .catch(() => message.error('Can not load the list of versions'))
      .finally(() =>
        setLoading(false)
      );
    }

  const handleOnInputVersionName = (e: any) => setNewVersion((prevState: any) => ({ ...prevState, name: e.target.value }))

  const hanldeOnChangeCheckbox = (e: any) => setNewVersion((prevState: any) => ({ ...prevState, isCurrent: e.target.checked }))
  
  const handleOnSaveNewVersion = () => {
    setOpenModalCreateNewVersion(() => {
      setLoading(true)
      return false
    })
    const params = {
      version: newVersion.name,
      marketplace: activedTab,
      isCurrentVersion: newVersion.isCurrent
    }
    createEstSalesVersion(params)
      .then(() => {
        message.success('Created version succesfully');
        navigate(`../${activedTab}/${newVersion.name}`);
      })
      .catch(() => message.error('Failed create version'))
      .finally(() => setLoading(false));
  }

  const handleOnChangeVersionName = (isCurrent: boolean = true, newVer?: string) => {
    setOpenModalChangeVersionName(() => {
      setNewVersion(newVersionState);
      setLoading(true);
      return false
    });
    let params: any = {
      version: selectedVersion,
      marketplace: activedTab,
      isCurrentVersion: isCurrent,
      newVersion: newVer || ''
    }
    
    return updateEstSalesVersion(params)
      .then(() => {
        message.success('Updated version succesfully');
        if (newVer) {
          navigate(`../${activedTab}/${newVer}`);
        } else {
          loadVersions(activedTab, version);
        }
      })
      .catch(() => message.error('Failed update version'))
      .finally(() => setLoading(false));
  };

  const handleOnSelectVersion = (version: string) => {
    navigate(`../${activedTab}/${version}`, { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>Estimate Sales</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Estimation Parameters"
        extra={[
          <Space key="versionn-select" style={{ marginRight: 30 }}>
            <label>Version:</label>
            <Select
              loading={loading}
              value={selectedVersion}
              options={versions}
              onChange={handleOnSelectVersion}
              style={{ width: 200 }}
            />
          </Space>,
          <Button key="overwrite-btn" onClick={() => setOpenModalChangeVersionName(true)}>Change this version name</Button>,
          <Button key="save-btn" type="primary" onClick={() => setOpenModalCreateNewVersion(true)}>
            Save as new version
          </Button>,
          <Button key="set-current-btn" type="primary" onClick={() => handleOnChangeVersionName(true)}>
            Set this version as current
          </Button>,
        ]}
        footer={
          <Tabs defaultActiveKey={activedTab} type="card" activeKey={activedTab} onChange={handleOnChangeTab}>
            {marketplaces.map(i => (
              <TabPane tab={i.label} key={i.key}>
                <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
                  <Space style={{ display:'flex', justifyContent: 'space-between' }}>
                    <Input.Group compact>
                      <Input style={{ width: 200 }} placeholder="Enter category name" onChange={handleOnInputCategory} />
                      <Button type="primary" onClick={handleOnAddCategory} disabled={categoryName.length === 0}>Add</Button>
                    </Input.Group>
                    <Typography><strong>Total: </strong>{dataTable?.length | 0}</Typography>
                  </Space>
                  <Table
                    className="table-striped-row"
                    columns={columns}
                    dataSource={dataTable}
                    loading={loadingTable}
                    pagination={false}
                    size="middle"
                  />
                </Space>
              </TabPane>
            ))}
          </Tabs>
        }
      />
      <Modal
        title="Create new version"
        visible={openModalCreateNewVersion}
        onOk={handleOnSaveNewVersion}
        onCancel={() => {
          setOpenModalCreateNewVersion(false);
          setNewVersion(newVersionState);
        }}
        okText="Save"
      >
        <Space direction="vertical">
          <Input style={{ width: 200 }} placeholder="Enter version name" value={newVersion.name} onChange={handleOnInputVersionName} />
          <Checkbox checked={newVersion.isCurrent} onChange={hanldeOnChangeCheckbox}>Mark it as a current version</Checkbox>
        </Space>
      </Modal>
      <Modal
        title="Change version name"
        visible={openModalChangeVersionName}
        onOk={() => {
          const versionIsCurrent = versions.filter((v: any) => v.value === version)[0].isCurrent;
          handleOnChangeVersionName(versionIsCurrent, newVersion.name);
        }}
        onCancel={() => {
          setOpenModalChangeVersionName(false);
          setNewVersion(newVersionState);
        }}
        okText="Save"
      >
        <Space direction="vertical">
          <Input style={{ width: 200 }} placeholder="Enter version name" value={newVersion.name} onChange={handleOnInputVersionName} />
        </Space>
      </Modal>
    </>
  )
}

export default EstimateSales;